// src/components/Reports.js

import React, { useState, useEffect, useRef } from "react";
import {
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";
import {
  Users,
  Clock,
  PieChart as PieChartIcon,
  FileText,
  Download,
  CheckCircle,
} from "lucide-react";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { getMergedData } from "../lib/dataUtils";
import Select from "react-select";
import ReportCharts from "./ReportCharts";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import StudentsList from "./StudentsList";
import FilterPill from "./ui/FilterPill";
import ReportPdf from "./ReportPdf";
import html2pdf from "html2pdf.js";

const Reports = () => {
  const [timeLogs, setTimeLogs] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [students, setStudents] = useState([]);
  const [programTypes, setProgramTypes] = useState([]);
  const [cohorts, setCohorts] = useState([]);

  // Filter states
  const [selectedProgramType, setSelectedProgramType] = useState("");
  const [selectedOpportunityType, setSelectedOpportunityType] = useState("");
  const [selectedCohort, setSelectedCohort] = useState("");
  const [selectedStudent, setSelectedStudent] = useState("");

  // Report data states
  const [totalTimeLogs, setTotalTimeLogs] = useState(0);
  const [totalHours, setTotalHours] = useState(0);
  const [totalApprovedHours, setTotalApprovedHours] = useState(0);
  const [statusData, setStatusData] = useState([]);
  const [opportunityTypeData, setOpportunityTypeData] = useState([]);
  const [studentData, setStudentData] = useState([]);
  const [monthlyTrendData, setMonthlyTrendData] = useState([]);
  const [topOpportunities, setTopOpportunities] = useState([]);
  const [programTypeData, setProgramTypeData] = useState([]);
  const [cohortData, setCohortData] = useState([]);
  const [schoolYearData, setSchoolYearData] = useState([]);

  const [isExporting, setIsExporting] = useState(false);

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = () => {
      try {
        // Fetch time logs
        const timeLogsData = getMergedData("timeLogs") || [];
        setTimeLogs(timeLogsData);

        // Fetch opportunities
        const opportunitiesData = getMergedData("opportunities") || [];
        setOpportunities(opportunitiesData);

        // Fetch students
        const studentsData = getMergedData("students") || [];
        setStudents(studentsData);

        // Extract unique program types and sort alphabetically
        const programs = [
          ...new Set(
            studentsData.map((student) => student.program).filter(Boolean)
          ),
        ].sort();
        setProgramTypes(programs);

        // Extract, deduplicate, and sort cohorts as strings
        const uniqueCohorts = [
          ...new Set(
            studentsData
              .map((student) => String(student.cohort))
              .filter(Boolean)
          ),
        ].sort();

        setCohorts(uniqueCohorts);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to fetch data. Please try again later.");
      }
    };

    fetchData();
  }, []);

  // Process data whenever timeLogs, opportunities, students, or filters change
  useEffect(() => {
    // Filter timeLogs based on selected filters
    let filteredTimeLogs = [...timeLogs].filter(
      (log) => log.status !== "rejected"
    ); // Filter out rejected logs first

    // Filter by Program Type
    if (selectedProgramType) {
      const filteredStudentIDs = students
        .filter((student) => student.program === selectedProgramType)
        .map((student) => student.studentId);
      filteredTimeLogs = filteredTimeLogs.filter((log) =>
        filteredStudentIDs.includes(log.studentId)
      );
    }

    // Filter by Opportunity Type
    if (selectedOpportunityType) {
      const filteredOpportunityIDs = opportunities
        .filter((op) => op.opportunityType === selectedOpportunityType)
        .map((op) => op.id);
      filteredTimeLogs = filteredTimeLogs.filter((log) =>
        filteredOpportunityIDs.includes(log.opportunityId)
      );
    }

    // Filter by Cohort
    if (selectedCohort) {
      const filteredStudentIDs = students
        .filter((student) => student.cohort === selectedCohort)
        .map((student) => student.studentId);
      filteredTimeLogs = filteredTimeLogs.filter((log) =>
        filteredStudentIDs.includes(log.studentId)
      );
    }

    // Filter by Student
    if (selectedStudent) {
      filteredTimeLogs = filteredTimeLogs.filter(
        (log) => log.studentId === selectedStudent
      );
    }

    // Total Time Logs (excluding rejected)
    setTotalTimeLogs(filteredTimeLogs.length);

    const calculateHoursBetween = (startTime, endTime) => {
      const [startHour, startMinute] = startTime.split(":").map(Number);
      const [endHour, endMinute] = endTime.split(":").map(Number);

      return endHour - startHour + (endMinute - startMinute) / 60;
    };

    // Total Hours
    const totalHours = Math.round(
      filteredTimeLogs.reduce((total, log) => {
        return total + calculateHoursBetween(log.startTime, log.endTime);
      }, 0)
    );
    // Total Approved Hours
    const totalApprovedHours = Math.round(
      filteredTimeLogs.reduce((total, log) => {
        if (log.status === "approved") {
          return total + calculateHoursBetween(log.startTime, log.endTime);
        }
        return total;
      }, 0)
    );
    setTotalHours(totalHours);
    setTotalApprovedHours(totalApprovedHours);

    // Time Logs by Status
    const statusCount = {};
    filteredTimeLogs.forEach((log) => {
      const status = log.status || "Unknown";
      statusCount[status] = (statusCount[status] || 0) + 1;
    });
    const statusDataArray = Object.keys(statusCount).map((status) => ({
      name: status.charAt(0).toUpperCase() + status.slice(1),
      value: statusCount[status],
    }));
    setStatusData(statusDataArray);

    // Time Logs by Opportunity Type
    const opportunityTypeCount = {};
    filteredTimeLogs.forEach((log) => {
      const opportunity = opportunities.find(
        (op) => op.id === log.opportunityId
      );
      const type = opportunity
        ? opportunity.opportunityType || "Unknown"
        : "Unknown";
      opportunityTypeCount[type] = (opportunityTypeCount[type] || 0) + 1;
    });
    const opportunityTypeDataArray = Object.keys(opportunityTypeCount).map(
      (type) => ({
        name: type,
        value: opportunityTypeCount[type],
      })
    );
    setOpportunityTypeData(opportunityTypeDataArray);

    // Time Logs by Student
    const studentCount = {};
    filteredTimeLogs.forEach((log) => {
      const student = log.studentId || "Unknown";
      studentCount[student] = (studentCount[student] || 0) + 1;
    });
    const studentDataArray = Object.keys(studentCount).map((student) => ({
      name: student,
      value: studentCount[student],
    }));
    setStudentData(studentDataArray);

    // Time Logs by Program Type
    const programCount = {};
    filteredTimeLogs.forEach((log) => {
      const student = students.find((s) => s.studentId === log.studentId);
      const program = student ? student.program || "Unknown" : "Unknown";
      programCount[program] = (programCount[program] || 0) + 1;
    });
    const programTypeDataArray = Object.keys(programCount).map((program) => ({
      name: program,
      value: programCount[program],
    }));
    setProgramTypeData(programTypeDataArray);

    // Time Logs by Cohort
    const cohortCount = {};
    filteredTimeLogs.forEach((log) => {
      const student = students.find((s) => s.studentId === log.studentId);
      const cohort = student ? student.cohort || "Unknown" : "Unknown";
      cohortCount[cohort] = (cohortCount[cohort] || 0) + 1;
    });
    const cohortDataArray = Object.keys(cohortCount).map((cohort) => ({
      name: cohort,
      value: cohortCount[cohort],
    }));
    setCohortData(cohortDataArray);

    // Time Logs by School Year
    const schoolYearCount = {};
    filteredTimeLogs.forEach((log) => {
      const student = students.find((s) => s.studentId === log.studentId);
      const schoolYear = student ? student.schoolYear || "Unknown" : "Unknown";
      schoolYearCount[schoolYear] = (schoolYearCount[schoolYear] || 0) + 1;
    });
    const schoolYearDataArray = Object.keys(schoolYearCount).map(
      (schoolYear) => ({
        name: schoolYear,
        value: schoolYearCount[schoolYear],
      })
    );
    setSchoolYearData(schoolYearDataArray);

    // Monthly Trend of Time Logs
    const monthlyCount = {};
    filteredTimeLogs.forEach((log) => {
      const date = new Date(log.date);
      const month = date.toLocaleString("default", {
        month: "short",
        year: "numeric",
      });
      monthlyCount[month] = (monthlyCount[month] || 0) + 1;
    });
    const monthlyTrendArray = Object.keys(monthlyCount).map((month) => ({
      month,
      logs: monthlyCount[month],
    }));
    // Sort the monthlyTrendArray by date
    monthlyTrendArray.sort((a, b) => {
      const dateA = new Date(a.month);
      const dateB = new Date(b.month);
      return dateA - dateB;
    });
    setMonthlyTrendData(monthlyTrendArray);

    // Top Opportunities by Time Logs
    const opportunityLogCount = {};
    filteredTimeLogs.forEach((log) => {
      const oppID = log.opportunityId;
      opportunityLogCount[oppID] = (opportunityLogCount[oppID] || 0) + 1;
    });

    const topOppsArray = Object.keys(opportunityLogCount)
      .map((oppID) => {
        const opp = opportunities.find((o) => o.id === parseInt(oppID));
        return {
          name: opp ? opp.hostName || "Unknown Host" : "Unknown Host",
          logs: opportunityLogCount[oppID],
        };
      })
      .sort((a, b) => b.logs - a.logs)
      .slice(0, 5); // Get only top 5

    setTopOpportunities(topOppsArray);
  }, [
    timeLogs,
    opportunities,
    students,
    selectedProgramType,
    selectedOpportunityType,
    selectedCohort,
    selectedStudent,
  ]);

  const exportPDF = () => {
    setIsExporting(true);

    const input = document.querySelector(".pdf-template");

    if (!input) {
      toast.error("PDF template not found");
      setIsExporting(false);
      return;
    }

    input.style.display = "block";

    const opt = {
      margin: [10, 10, 10, 10],
      filename: `wbl_students_list_report_${new Date()
        .toLocaleDateString()
        .replace(/\//g, "-")}.pdf`,
      image: { type: "jpeg", quality: 1.5 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        imageTimeout: 0,
      },
      jsPDF: {
        unit: "mm",
        format: "a4",
        orientation: "portrait",
      },
    };

    html2pdf()
      .set(opt)
      .from(input)
      .save()
      // .output("dataurlnewwindow", {
      //   filename: `wbl_report_${new Date().toISOString().split("T")[0]}.pdf`,
      // })
      .then(() => {
        input.style.display = "none";

        setIsExporting(false);
      })
      .catch((error) => {
        console.error("PDF export error:", error);
        setIsExporting(false);
      });
  };

  const filteredStudents = students.filter((student) => {
    const studentTimeLogs = timeLogs.filter((log) => {
      const matchesStudent = log.studentId === student.studentId;
      const matchesOpportunityType = selectedOpportunityType
        ? opportunities.find(
            (op) =>
              op.id === log.opportunityId &&
              op.opportunityType === selectedOpportunityType
          )
        : true;
      return matchesStudent && matchesOpportunityType;
    });

    // Only include student if they have matching time logs and meet other criteria
    const hasMatchingLogs = studentTimeLogs.length > 0;
    const matchesProgram = selectedProgramType
      ? student.program === selectedProgramType
      : true;
    const matchesCohort = selectedCohort
      ? student.cohort === selectedCohort
      : true;
    const matchesSelectedStudent = selectedStudent
      ? student.studentId === selectedStudent
      : true;

    return (
      hasMatchingLogs &&
      matchesProgram &&
      matchesCohort &&
      matchesSelectedStudent
    );
  });

  // Create options for select components
  const programTypeOptions = programTypes.map((program) => ({
    value: program,
    label: program,
  }));

  const opportunityTypeOptions = opportunities
    .map((op) => op.opportunityType)
    .filter((type, index, self) => type && self.indexOf(type) === index)
    .sort()
    .map((type) => ({
      value: type,
      label: type,
    }));

  const cohortOptions = cohorts.map((cohort) => ({
    value: cohort,
    label: cohort,
  }));

  const studentOptions = filteredStudents
    .sort((a, b) =>
      (a.firstName + a.lastName)?.localeCompare(b.firstName + b.lastName)
    )
    .map((student) => ({
      value: student.studentId,
      label: `${student.firstName} ${student.lastName} [${student.studentId}]`,
      student: student,
    }));

  const getActiveFilters = () => {
    const filters = [];
    if (selectedProgramType) filters.push(`Program: ${selectedProgramType}`);
    if (selectedOpportunityType)
      filters.push(`Opportunity: ${selectedOpportunityType}`);
    if (selectedCohort) filters.push(`Cohort: ${selectedCohort}`);
    if (selectedStudent) {
      const studentLabel = studentOptions.find(
        (opt) => opt.value === selectedStudent
      )?.label;
      filters.push(`Student: ${studentLabel || selectedStudent}`);
    }
    return filters;
  };

  const metrics = {
    "Total Time Logs": {
      value: totalTimeLogs,
      icon: FileText,
      color: "text-blue-600",
      bgColor: "bg-blue-100",
    },
    "Approved Logs": {
      value: statusData.find((item) => item.name === "Approved")?.value || 0,
      icon: CheckCircle,
      color: "text-purple-600",
      bgColor: "bg-purple-100",
    },
    "Approved Hours": {
      value: totalApprovedHours,
      icon: Clock,
      color: "text-green-600",
      bgColor: "bg-green-100",
    },
    Students: {
      value: filteredStudents.length,
      icon: Users,
      color: "text-yellow-600",
      bgColor: "bg-yellow-100",
    },
  };

  return (
    <div className="p-6 min-h-screen">
      <div className="flex flex-col md:flex-row md:space-x-4 mb-6 justify-between items-center">
        <h2 className="text-3xl font-bold text-gray-800">Reports</h2>
        <button
          onClick={exportPDF}
          disabled={isExporting}
          className={`${
            isExporting ? "bg-gray-400" : "bg-green-600 hover:bg-green-700"
          } text-white font-semibold py-2 px-4 rounded-md flex items-center shadow-md transition duration-300`}
        >
          <Download className="mr-2" size={20} />
          {isExporting ? "Exporting..." : "Export students list as PDF"}
        </button>
      </div>

      <div className="filter-controls space-y-4 px-2">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="w-full">
            <Select
              isClearable
              options={programTypeOptions}
              value={
                programTypeOptions.find(
                  (option) => option.value === selectedProgramType
                ) || null
              }
              onChange={(option) =>
                setSelectedProgramType(option ? option.value : "")
              }
              placeholder="All Programs"
              classNamePrefix="select"
              className="w-full"
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: '42px',
                  background: 'white',
                  borderColor: '#E5E7EB',
                  '&:hover': {
                    borderColor: '#9CA3AF'
                  }
                }),
                placeholder: (base) => ({
                  ...base,
                  color: '#6B7280'
                })
              }}
            />
          </div>

          <div className="w-full">
            <Select
              isClearable
              options={opportunityTypeOptions}
              value={
                opportunityTypeOptions.find(
                  (option) => option.value === selectedOpportunityType
                ) || null
              }
              onChange={(option) =>
                setSelectedOpportunityType(option ? option.value : "")
              }
              placeholder="All Opportunity Types"
              classNamePrefix="select"
              className="w-full"
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: '42px',
                  background: 'white',
                  borderColor: '#E5E7EB',
                  '&:hover': {
                    borderColor: '#9CA3AF'
                  }
                }),
                placeholder: (base) => ({
                  ...base,
                  color: '#6B7280'
                })
              }}
            />
          </div>

          <div className="w-full">
            <Select
              isClearable
              options={cohortOptions}
              value={
                cohortOptions.find((option) => option.value === selectedCohort) ||
                null
              }
              onChange={(option) => setSelectedCohort(option ? option.value : "")}
              placeholder="All Cohorts"
              classNamePrefix="select"
              className="w-full"
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: '42px',
                  background: 'white',
                  borderColor: '#E5E7EB',
                  '&:hover': {
                    borderColor: '#9CA3AF'
                  }
                }),
                placeholder: (base) => ({
                  ...base,
                  color: '#6B7280'
                })
              }}
            />
          </div>

          <div className="w-full">
            <Select
              isClearable
              options={studentOptions}
              value={
                studentOptions.find(
                  (option) => option.value === selectedStudent
                ) || null
              }
              onChange={(option) =>
                setSelectedStudent(option ? option.value : "")
              }
              placeholder="All Students"
              classNamePrefix="select"
              className="w-full"
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: '42px',
                  background: 'white',
                  borderColor: '#E5E7EB',
                  '&:hover': {
                    borderColor: '#9CA3AF'
                  }
                }),
                placeholder: (base) => ({
                  ...base,
                  color: '#6B7280'
                })
              }}
            />
          </div>
        </div>
      </div>

      <div className="export-content space-y-6">
        <div className="mt-6 mb-0 active-filters">
          {(selectedProgramType ||
            selectedOpportunityType ||
            selectedCohort ||
            selectedStudent) && (
            <div className="flex flex-wrap items-center">
              <span className="text-sm text-gray-500 mr-2">
                Active Filters:
              </span>
              {selectedProgramType && (
                <FilterPill
                  label={`Program: ${selectedProgramType}`}
                  onRemove={() => setSelectedProgramType("")}
                />
              )}
              {selectedOpportunityType && (
                <FilterPill
                  label={`Opportunity: ${selectedOpportunityType}`}
                  onRemove={() => setSelectedOpportunityType("")}
                />
              )}
              {selectedCohort && (
                <FilterPill
                  label={`Cohort: ${selectedCohort}`}
                  onRemove={() => setSelectedCohort("")}
                />
              )}
              {selectedStudent && (
                <FilterPill
                  label={`Student: ${
                    studentOptions.find(
                      (option) => option.value === selectedStudent
                    )?.label || selectedStudent
                  }`}
                  onRemove={() => setSelectedStudent("")}
                />
              )}
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 metrics-grid px-2">
            {/* Total Time Logs */}
            <div className="bg-white rounded-lg shadow-md p-4 flex items-center">
              <div className="bg-blue-100 p-3 rounded-full flex-shrink-0">
                <FileText className="text-blue-600" size={20} />
              </div>
              <div className="ml-3 min-w-0">
                <p className="text-gray-500 text-sm whitespace-nowrap">Total Time Logs</p>
                <p className="text-lg font-bold text-gray-800">
                  {totalTimeLogs}
                </p>
              </div>
            </div>

            {/* Time Logs by Status */}
            <div className="bg-white rounded-lg shadow-md p-4 flex items-center">
              <div className="bg-purple-100 p-3 rounded-full flex-shrink-0">
                <PieChartIcon className="text-purple-600" size={20} />
              </div>
              <div className="ml-3 min-w-0">
                <p className="text-gray-500 text-sm whitespace-nowrap">Approved Logs</p>
                <p className="text-lg font-bold text-gray-800">
                  {statusData.find((item) => item.name === "Approved")?.value ||
                    0}
                </p>
              </div>
            </div>

            {/* Total Hours Logged */}
            <div className="bg-white rounded-lg shadow-md p-4 flex items-center">
              <div className="bg-green-100 p-3 rounded-full flex-shrink-0">
                <Clock className="text-green-600" size={20} />
              </div>
              <div className="ml-3 min-w-0">
                <p className="text-gray-500 text-sm whitespace-nowrap">Approved Hours</p>
                <p className="text-lg font-bold text-gray-800">
                  {totalApprovedHours}
                </p>
              </div>
            </div>

            {/* Total Students based on filters */}
            <div className="bg-white rounded-lg shadow-md p-4 flex items-center">
              <div className="bg-yellow-100 p-3 rounded-full flex-shrink-0">
                <Users className="text-yellow-600" size={20} />
              </div>
              <div className="ml-3 min-w-0">
                <p className="text-gray-500 text-sm whitespace-nowrap">Students</p>
                <p className="text-lg font-bold text-gray-800">
                  {filteredStudents.length}
                </p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="overview">
                <span className="text-sm text-gray-500 font-medium uppercase hover:text-gray-700 transition-colors duration-200 tracking-wider">
                  Overview
                </span>
              </TabsTrigger>
              <TabsTrigger value="students">
                <span className="text-sm text-gray-500 font-medium uppercase hover:text-gray-700 transition-colors duration-200 tracking-wider">
                  Students List
                </span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview">
              <ReportCharts
                opportunityTypeData={opportunityTypeData}
                studentData={studentData}
                programTypeData={programTypeData}
                cohortData={cohortData}
              />

              {/* Monthly Trend of Time Logs */}
              <div className="bg-white rounded-lg shadow-md p-6 mt-8">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">
                  Monthly Trend of Time Logs
                </h3>
                {monthlyTrendData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={monthlyTrendData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="logs"
                        stroke="#10B981"
                        name="Time Logs"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-gray-500">No data available.</p>
                )}
              </div>

              {/* Top Opportunities */}
              <div className="bg-white rounded-lg shadow-md p-6 mt-8">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">
                  Top 5 Opportunities by Time Logs
                </h3>
                {topOpportunities.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={topOpportunities}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="name"
                        tick={{ fontSize: 12 }}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                        scale="auto"
                        xAxisId={0}
                      />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="logs" fill="#EF4444" name="Time Logs" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-gray-500">No data available.</p>
                )}
              </div>
            </TabsContent>

            <TabsContent value="students" data-value="students">
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">
                  Students List
                </h3>
                <StudentsList
                  students={filteredStudents}
                  opportunities={opportunities}
                  timeLogs={timeLogs}
                />
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <ReportPdf
        students={filteredStudents}
        opportunities={opportunities}
        timeLogs={timeLogs}
        activeFilters={getActiveFilters()}
        metrics={metrics}
      />
    </div>
  );
};

export default Reports;